import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingBag, Package } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { addReservation } from '../utils/firestoreService';

export const Cart = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, clearCart, getTotalPrice } = useCart();
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) {
      toast.error('¡Tu carrito está vacío!');
      return;
    }

    setSubmitting(true);
    try {
      const reservation = {
        products: cart.map((item) => ({
          productId: item.id,
          productName: item.name,
          quantity: item.quantity,
          price: item.price,
        })),
        customerInfo,
        totalAmount: getTotalPrice(),
        status: 'pending',
        createdAt: new Date().toISOString(),
      };

      await addReservation(reservation);
      toast.success('¡Reserva realizada con éxito! Te contactaremos pronto.');
      clearCart();
      setCustomerInfo({ name: '', email: '', phone: '', address: '' });
      navigate('/');
    } catch (error) {
      console.error('Error creating reservation:', error);
      toast.error('Error al procesar la reserva. Verifica las reglas de Firestore.');
    } finally {
      setSubmitting(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen py-20">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <Package className="w-24 h-24 mx-auto mb-6 text-[#9CA3AF]" />
          <h2 className="text-3xl font-fredoka font-bold text-[#1A1A2E] mb-4" data-testid="empty-cart-message">
            Tu carrito está vacío
          </h2>
          <p className="text-lg text-[#4A4A6A] font-quicksand mb-8">
            ¡Aún no has agregado ningún juguete!
          </p>
          <button
            onClick={() => navigate('/products')}
            className="bg-[#FF6B00] text-white px-8 py-4 rounded-full font-fredoka font-bold text-lg hover:bg-[#FF6B00]/90 hover:scale-105 transition-all duration-300 shadow-lg"
            data-testid="go-shopping-button"
          >
            Ir de Compras
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl sm:text-5xl font-fredoka font-bold text-[#1A1A2E] mb-8" data-testid="cart-page-title">
          <ShoppingBag className="inline-block w-10 h-10 mr-3 mb-2" />
          Carrito de Reservas
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-2xl p-6 shadow-lg flex gap-6"
                data-testid={`cart-item-${item.id}`}
              >
                <img
                  src={item.imageUrl}
                  alt={item.name}
                  className="w-24 h-24 object-cover rounded-xl"
                />
                <div className="flex-1">
                  <h3 className="font-fredoka font-bold text-xl text-[#1A1A2E] mb-2">
                    {item.name}
                  </h3>
                  <p className="text-[#00C2CB] font-quicksand font-bold text-lg mb-4">
                    ${item.price.toLocaleString()}
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="px-3 py-1 bg-slate-100 hover:bg-slate-200"
                        data-testid={`decrease-cart-quantity-${item.id}`}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="px-4 font-quicksand font-bold" data-testid={`cart-quantity-${item.id}`}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="px-3 py-1 bg-slate-100 hover:bg-slate-200"
                        data-testid={`increase-cart-quantity-${item.id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="text-red-500 hover:text-red-600 p-2"
                      data-testid={`remove-cart-item-${item.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[#1A1A2E] font-fredoka font-bold text-xl" data-testid={`cart-item-total-${item.id}`}>
                    ${(item.price * item.quantity).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Reservation Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-lg sticky top-24">
              <h2 className="font-fredoka font-bold text-2xl text-[#1A1A2E] mb-6">
                Información de Contacto
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="font-quicksand font-semibold">
                    Nombre completo *
                  </Label>
                  <Input
                    id="name"
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                    required
                    className="rounded-xl font-quicksand"
                    data-testid="customer-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="font-quicksand font-semibold">
                    Correo electrónico *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                    required
                    className="rounded-xl font-quicksand"
                    data-testid="customer-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="font-quicksand font-semibold">
                    Teléfono *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                    required
                    className="rounded-xl font-quicksand"
                    data-testid="customer-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="address" className="font-quicksand font-semibold">
                    Dirección *
                  </Label>
                  <Textarea
                    id="address"
                    value={customerInfo.address}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })}
                    required
                    className="rounded-xl font-quicksand"
                    rows={3}
                    data-testid="customer-address-input"
                  />
                </div>

                <div className="border-t border-slate-200 pt-4 mt-6">
                  <div className="flex justify-between items-center mb-6">
                    <span className="font-quicksand font-semibold text-lg text-[#1A1A2E]">
                      Total:
                    </span>
                    <span className="font-fredoka font-bold text-3xl text-[#00C2CB]" data-testid="cart-total">
                      ${getTotalPrice().toLocaleString()}
                    </span>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#FF6B00] text-white px-8 py-4 rounded-full font-fredoka font-bold text-lg hover:bg-[#FF6B00]/90 hover:scale-105 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    data-testid="reserve-toys-button"
                  >
                    {submitting ? 'Procesando...' : 'Reservar Juguetes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};